import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Card, Spinner, Alert, Table, Badge, Accordion, Collapse } from 'react-bootstrap';
import ReportButton from './ReportButton';
import './CheapProducts.css';
import { useHomeLocation } from '../contexts/HomeLocationContext';

const CheapProducts = () => {
  const navigate = useNavigate();
  const { homeLocation } = useHomeLocation();
  const [maxDistance, setMaxDistance] = useState(2.0);
  const [discountThreshold, setDiscountThreshold] = useState(10.0);
  const [minStores, setMinStores] = useState(3);
  const [minSavings, setMinSavings] = useState(0.0);
  const [criterion, setCriterion] = useState('percentage');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState(new Set());

  const handleSearch = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      setError(null);
      setHasSearched(true);

      const params = new URLSearchParams({
        maxDistance,
        discountThreshold: discountThreshold / 100,
        minStores,
        criterion,
        minSavings,
      });
      if (homeLocation) {
        params.set('homeLat', homeLocation.lat);
        params.set('homeLon', homeLocation.lon);
      }
      const response = await fetch(`/api/cheap-products?${params}`);

      if (!response.ok) {
        throw new Error('Failed to fetch cheap products');
      }

      const data = await response.json();
      setResults(data);
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching cheap products:', err);
      setError('Failed to find cheap products. Please try again.');
      setIsLoading(false);
    }
  };

  const formatPrice = (price) => {
    return `₪${price.toFixed(2)}`;
  };

  const formatDiscount = (discount) => {
    return `${discount.toFixed(1)}%`;
  };

  const toggleProductExpansion = (storeId, itemCode) => {
    const key = `${storeId}-${itemCode}`;
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  return (
    <div className="cheap-products-container">
      <Card className="mb-4">
        <Card.Header>
          <h4 className="mb-0" style={{ fontSize: 'var(--font-title)', fontWeight: 600, letterSpacing: '-0.3px' }}>Find Cheap Products in Your Area</h4>
        </Card.Header>
        <Card.Body>
          <p className="text-muted">
            Find products that are significantly cheaper in nearby stores compared to the area average.
          </p>

          <Form onSubmit={handleSearch}>
            <Form.Group className="mb-3">
              <Form.Label>
                Maximum Distance (km){' '}
                {homeLocation && (
                  <span className="badge bg-primary ms-1" style={{ fontSize: '11px', fontWeight: 500 }}>
                    Custom location
                  </span>
                )}
              </Form.Label>
              <Form.Control
                type="number"
                step="0.1"
                min="0.5"
                max="20"
                value={maxDistance}
                onChange={(e) => setMaxDistance(parseFloat(e.target.value))}
              />
              <Form.Text className="text-muted">
                {homeLocation
                  ? `From: ${homeLocation.label}`
                  : 'Only include stores within this distance from your home address.'}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Search Criterion</Form.Label>
              <Form.Select
                value={criterion}
                onChange={(e) => setCriterion(e.target.value)}
              >
                <option value="percentage">Percentage Below Average</option>
                <option value="lowest">Lowest Price Only</option>
              </Form.Select>
              <Form.Text className="text-muted">
                {criterion === 'percentage'
                  ? 'Find products below average price by a certain percentage.'
                  : 'Find products with the absolute lowest price across all stores.'}
              </Form.Text>
            </Form.Group>

            {criterion === 'percentage' && (
              <Form.Group className="mb-3">
                <Form.Label>Minimum Discount (%)</Form.Label>
                <Form.Control
                  type="number"
                  step="1"
                  min="5"
                  max="50"
                  value={discountThreshold}
                  onChange={(e) => setDiscountThreshold(parseFloat(e.target.value))}
                />
                <Form.Text className="text-muted">
                  Only show products that are at least this much cheaper than the average price.
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Minimum Stores</Form.Label>
              <Form.Control
                type="number"
                min="2"
                max="10"
                value={minStores}
                onChange={(e) => setMinStores(parseInt(e.target.value))}
              />
              <Form.Text className="text-muted">
                Only include products that are available in at least this many stores.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Minimum Absolute Savings (₪)</Form.Label>
              <Form.Control
                type="number"
                step="0.5"
                min="0"
                max="100"
                value={minSavings}
                onChange={(e) => setMinSavings(parseFloat(e.target.value))}
              />
              <Form.Text className="text-muted">
                Only show products where you save at least this amount in NIS (0 = no minimum).
              </Form.Text>
            </Form.Group>

            <div className="d-grid">
              <Button variant="success" type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Finding Cheap Products...
                  </>
                ) : (
                  'Find Cheap Products'
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {results && (
        <Card>
          <Card.Header className="bg-light">
            <h5 className="mb-0">
              Found {results.stores.length} stores with cheap products
            </h5>
            <small className="text-muted">
              Within {maxDistance} km |
              Criterion: {criterion === 'lowest' ? 'Lowest price' : 'Percentage below average'}
            </small>
          </Card.Header>
          <Card.Body>
            {results.stores.length === 0 ? (
              <Alert variant="info">
                No cheap products found with the selected criteria. Try increasing the distance or reducing the discount threshold.
              </Alert>
            ) : (
              <Accordion defaultActiveKey="0">
                {results.stores.map((store, index) => (
                  <Accordion.Item eventKey={index.toString()} key={store.storeId}>
                    <Accordion.Header>
                      <div className="w-100 d-flex justify-content-between align-items-center me-3">
                        <div>
                          <strong>{store.storeName}</strong> <small className="text-muted">({store.storeId})</small>
                          <br />
                          <small className="text-muted">
                            {store.city} | <span className={store.distance.includes('(משוער)') ? 'text-warning fst-italic' : ''}>{store.distance}</span>
                          </small>
                          {store.llmImprovedAddress && store.llmImprovedAddress !== store.address && (
                            <>
                              <br />
                              <small className="text-muted">
                                <span style={{ opacity: 0.7 }}>Original: {store.address}, {store.city}</span>
                              </small>
                              <br />
                              <small className="text-success">
                                <strong>LLM-improved: {store.llmImprovedAddress}</strong>
                              </small>
                            </>
                          )}
                          {!store.llmImprovedAddress && store.address && (
                            <>
                              <br />
                              <small className="text-muted">
                                Address: {store.address}, {store.city}
                              </small>
                            </>
                          )}
                        </div>
                        <Badge bg="success" pill>
                          {store.cheapProductsCount} cheap products
                        </Badge>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <Table striped hover responsive>
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Price</th>
                            <th>Average Price</th>
                            <th>Discount</th>
                            <th>Savings</th>
                            <th>Available In</th>
                          </tr>
                        </thead>
                        <tbody>
                          {store.products.map((product) => {
                            const productKey = `${store.storeId}-${product.itemCode}`;
                            const isExpanded = expandedProducts.has(productKey);

                            return (
                              <React.Fragment key={product.itemCode}>
                                <tr
                                  className="product-row"
                                  onClick={() => toggleProductExpansion(store.storeId, product.itemCode)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <td>
                                    <div dir="rtl">{product.itemName}</div>
                                    <small className="text-muted">
                                      {product.manufacturerName}
                                      {product.itemCode && (
                                        <> | ID: {product.itemCode}</>
                                      )}
                                    </small>
                                    {product.itemCode && (
                                      <div className="mt-1">
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          style={{ fontSize: '0.7rem', padding: '1px 6px' }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/?tab=search&q=${product.itemCode}`);
                                          }}
                                        >
                                          Barcode Lookup
                                        </Button>
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ fontWeight: 600, color: '#1c7a3b' }}>
                                    {product.hasPromotion ? (
                                      <>
                                        {formatPrice(product.price)}
                                        {product.promotionType === 'conditional_discount' ? (
                                          <Badge
                                            bg="light"
                                            text="secondary"
                                            className="ms-2 promo-badge"
                                            style={{ border: '1px solid #ccc' }}
                                            title={product.promotionDescription || ''}
                                          >
                                            {product.promotionMinBasket ? `עם קנייה מ-₪${product.promotionMinBasket}` : 'בתנאי'}
                                          </Badge>
                                        ) : (
                                          <Badge bg="danger" className="ms-2 promo-badge">
                                            PROMO
                                          </Badge>
                                        )}
                                        <br />
                                        <small className="text-muted text-decoration-line-through">
                                          Regular: {formatPrice(product.regularPrice)}
                                        </small>
                                      </>
                                    ) : (
                                      formatPrice(product.price)
                                    )}
                                    <ReportButton itemCode={product.itemCode} storeId={store.storeId} chain={store.chain} price={product.price} itemName={product.itemName} />
                                  </td>
                                  <td className="text-muted">
                                    {formatPrice(product.avgPrice)}
                                  </td>
                                  <td>
                                    <Badge bg="success" className="discount-badge">
                                      {formatDiscount(product.discountPct)}
                                    </Badge>
                                  </td>
                                  <td style={{ color: '#1c7a3b' }}>
                                    {formatPrice(product.savings)}
                                  </td>
                                  <td>
                                    <small>
                                      {product.storesCount} stores {isExpanded ? '▼' : '▶'}
                                    </small>
                                  </td>
                                </tr>
                                {isExpanded && product.pricesByStore && (
                                  <tr>
                                    <td colSpan="6" className="p-0">
                                      <Collapse in={isExpanded}>
                                        <div className="price-comparison-container">
                                          <Table size="sm" className="mb-0">
                                            <thead>
                                              <tr>
                                                <th>Store</th>
                                                <th>Final Price</th>
                                                <th>Difference</th>
                                                <th>Distance</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {product.pricesByStore.map((storePrice) => (
                                                <tr
                                                  key={storePrice.storeId}
                                                  className={storePrice.storeId === store.storeId ? 'table-success' : ''}
                                                >
                                                  <td>
                                                    <small dir="rtl">{storePrice.storeName} ({storePrice.storeId})</small>
                                                  </td>
                                                  <td>
                                                    <small className={storePrice.storeId === store.storeId ? 'fw-bold' : ''}>
                                                      {formatPrice(storePrice.price)}
                                                      {storePrice.hasPromotion && (
                                                        <>
                                                          <Badge bg="danger" className="ms-1 promo-badge">
                                                            PROMO
                                                          </Badge>
                                                          <br />
                                                          <span className="text-muted text-decoration-line-through" style={{ fontSize: '0.75rem' }}>
                                                            {formatPrice(storePrice.regularPrice)}
                                                          </span>
                                                        </>
                                                      )}
                                                    </small>
                                                    <ReportButton itemCode={product.itemCode} storeId={storePrice.storeId} chain={storePrice.chain} price={storePrice.price} itemName={product.itemName} />
                                                  </td>
                                                  <td>
                                                    <small style={{ color: storePrice.price > product.price ? 'var(--apple-accent)' : '#1c7a3b' }}>
                                                      {storePrice.price > product.price
                                                        ? `+${formatPrice(storePrice.price - product.price)}`
                                                        : storePrice.price < product.price
                                                        ? `-${formatPrice(product.price - storePrice.price)}`
                                                        : '—'}
                                                    </small>
                                                  </td>
                                                  <td>
                                                    <small className={storePrice.distance.includes('(משוער)') ? 'text-warning fst-italic' : 'text-muted'}>{storePrice.distance}</small>
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </Table>
                                        </div>
                                      </Collapse>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </Table>
                      {store.cheapProductsCount > store.products.length && (
                        <Alert variant="info" className="mb-0">
                          Showing {store.products.length} of {store.cheapProductsCount} cheap products.
                        </Alert>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            )}
          </Card.Body>
        </Card>
      )}

      {hasSearched && !isLoading && !results && !error && (
        <Alert variant="info">
          No results yet. Try adjusting your search parameters.
        </Alert>
      )}
    </div>
  );
};

export default CheapProducts;
